import { z } from 'zod';

/**
 * Entry-marketplace schemas.
 *
 * `degraded` facet arrays stay `z.array(z.string())` (never enum'd) so the backend can add facets
 * without breaking the parse; no code asserts their exact contents.
 *
 * A field the producer may not have deployed is wire-optional so the response parses either way.
 * Fields whose parse failure costs something specific — a dropped row, a blanked panel — say so.
 */

const IsoDateSchema = z.string().datetime({ offset: true });
const BasisPointsSchema = z.number().int().min(0).max(10_000);
const CentsSchema = z.number().int().nonnegative();
const IdSchema = z.string().min(1);
const OfferFloorPercentSchema = z.number().min(0).max(10_000);

const GroupViewUserSchema = z.object({
  id: IdSchema,
  name: z.string(),
  handle: z.string(),
  avatarUrl: z.string().nullable(),
});

// The marketplace cannot transact against ryp/ofp contests, so a foreign source fails the parse.
const GroupInviteContestSchema = z.object({
  source: z.literal('splash'),
  name: z.string(),
  thumbnailUrl: z.string().nullable(),
  commissioner: GroupViewUserSchema.nullable(),
  entryFeeCents: CentsSchema,
  prizePoolCents: CentsSchema,
});

const GroupInviteBaseSchema = z.object({
  id: IdSchema,
  entryId: IdSchema,
  contestId: IdSchema,
  organizerUserId: IdSchema,
  recipientUserId: IdSchema,
  shareBps: BasisPointsSchema,
  priceCents: CentsSchema,
  locksAt: IsoDateSchema,
  createdAt: IsoDateSchema,
  resolvedAt: IsoDateSchema.nullable(),
  assignsPickControl: z.boolean(),
  organizer: GroupViewUserSchema,
  recipient: GroupViewUserSchema,
});

const groupInviteVariants = <Shape extends z.ZodRawShape>(base: z.ZodObject<Shape, 'strip'>) =>
  [
    base.extend({ type: z.literal('pending'), status: z.literal('pending') }),
    base.extend({
      type: z.literal('accepted'),
      status: z.literal('accepted'),
      transactionId: IdSchema.nullable(),
    }),
    // The resolved terminal set includes `rescinded` (the organizer took the invite back), and
    // `revertedVia` carries the full backend reason set — a row with any of these must never drop.
    base.extend({
      type: z.literal('resolved'),
      status: z.enum(['declined', 'cancelled', 'rescinded']),
    }),
    base.extend({
      type: z.literal('reverted'),
      status: z.literal('reverted'),
      revertedVia: z.enum([
        'pick_lock',
        'admin',
        'splits_disabled',
        'hold_reconcile',
        'entry_cancelled',
      ]),
    }),
  ] as const;

// Only recipient reads embed `contest`; write paths and the organizer's `invites` omit it.
export const GroupInviteSchema = z.discriminatedUnion('type', [
  ...groupInviteVariants(GroupInviteBaseSchema),
]);

export const GroupInviteWithContestSchema = z.discriminatedUnion('type', [
  ...groupInviteVariants(
    GroupInviteBaseSchema.extend({ contest: GroupInviteContestSchema.nullable() })
  ),
]);

const GroupEligibilityReasonSchema = z.enum([
  'not_full_owner',
  'shares_transferred',
  'shares_listed',
  'pick_lock_passed',
  'marketplace_not_enabled',
  'entry_not_alive',
]);

const GroupEligibilitySchema = z.object({
  eligible: z.boolean(),
  reason: GroupEligibilityReasonSchema.nullable().catch(null),
});

const GroupEntryShareholderSchema = z.object({
  userId: IdSchema,
  shareBps: BasisPointsSchema,
  // Ownership after pending invites settle; null when unchanged (render `proposedShareBps ?? shareBps`).
  proposedShareBps: BasisPointsSchema.nullable(),
  user: GroupViewUserSchema,
});

// Share-safe: no price or payment detail.
const GroupEntryPendingInviteeSchema = z.object({
  userId: IdSchema,
  shareBps: BasisPointsSchema,
  user: GroupViewUserSchema,
});

const GroupEntryViewBaseShape = {
  entryId: IdSchema,
  totalShareholders: z.number().int().nonnegative(),
  splitEligibility: GroupEligibilitySchema.nullable(),
};

export const GroupEntryViewSchema = z.discriminatedUnion('viewerRole', [
  z.object({
    ...GroupEntryViewBaseShape,
    viewerRole: z.literal('organizer'),
    pickControllerUserId: IdSchema,
    // The projection reflects the state once pending invites settle.
    pickController: GroupViewUserSchema,
    proposedPickControllerUserId: IdSchema,
    proposedPickController: GroupViewUserSchema,
    shareholders: z.array(GroupEntryShareholderSchema),
    invites: z.array(GroupInviteSchema),
    pendingInvitees: z.array(GroupEntryPendingInviteeSchema),
  }),
  z.object({
    ...GroupEntryViewBaseShape,
    viewerRole: z.enum(['co_owner', 'invitee']),
    pickControllerUserId: IdSchema,
    pickController: GroupViewUserSchema,
    proposedPickControllerUserId: IdSchema,
    proposedPickController: GroupViewUserSchema,
    shareholders: z.array(GroupEntryShareholderSchema),
    pendingInvitees: z.array(GroupEntryPendingInviteeSchema),
  }),
  z.object({
    ...GroupEntryViewBaseShape,
    viewerRole: z.literal('none'),
  }),
]);

export const CreateGroupInvitesRequestSchema = z.object({
  entryId: IdSchema,
  invites: z.array(
    z.object({
      recipientUserId: IdSchema,
      shareBps: BasisPointsSchema,
      priceCents: CentsSchema,
    })
  ),
  pickControllerRecipientUserId: IdSchema.nullable().optional(),
});

// Marketplace display identity is handle-only — never a real/channel name.
const MarketplaceUserSchema = z.object({
  id: IdSchema,
  handle: z.string(),
  avatarUrl: z.string().nullable(),
});

// Browse-row commissioner: always present, per-field sentinels on an enrichment miss
// (`handle: 'unknown'` is the miss signal; `id` may be null even when handle is real).
const CommissionerRefSchema = z.object({
  id: IdSchema.nullable(),
  handle: z.string(),
  avatarUrl: z.string().nullable(),
});

// A revealed pick carries the team identity (teamName/teamAlias) alongside the ids. gameId/slateId
// stay optional (forward-lenient — the FE only keys on them); teamId/teamName/teamAlias are the
// shipped required set.
const PublicListingPickSchema = z.object({
  teamId: IdSchema,
  teamName: z.string(),
  teamAlias: z.string(),
  gameId: IdSchema.optional(),
  slateId: IdSchema.optional(),
  // The slate label for the pick-history row, e.g. "Week 1". Wire-optional + nullable: populated
  // on listing detail + entry view, null where the producer can't resolve it.
  slateName: z.string().nullable().optional(),
});

const PublicListingEntrySchema = z.object({
  id: IdSchema,
  order: z.number().int(),
  livesRemaining: z.number().int(),
  // Back the Buy-now team-picks grid (one row per listing): usedTeamIds = revealed picks,
  // availableTeamIds = universe teams within the reuse limit. Optional because this is the base of
  // the list rows and getListings' parseRows drops any row that fails to parse — a required field
  // the producer has not shipped would silently drop every listing (cross-repo deploys unordered).
  usedTeamIds: z.array(z.string()).optional(),
  availableTeamIds: z.array(z.string()).optional(),
});

// The contest's current slate. picksRevealed is authoritative — read it directly, never
// derive reveal from picksLockAt. null when no slate is active.
const PublicCurrentSlateSchema = z.object({
  id: IdSchema,
  // The slate label for the buy-now Slate cell. Wire-optional + nullable: the producer returns it
  // on listing detail / entry view but leaves it null on browse by design.
  name: z.string().nullable().optional(),
  picksLockAt: IsoDateSchema,
  picksRevealed: z.boolean(),
});

// The contest's marketplace-status OBJECT (open | closed{frozen | no_upcoming_slate |
// not_enabled}) — NOT a bare string. The `status` discriminator keeps the open/closed fields
// optional so an unrecognized status or reason still parses instead of rejecting the whole
// object and blanking every panel.
const MarketplaceStatusSchema = z.object({
  status: z.string(),
  contestId: z.string().optional(),
  currentSlateId: z.string().nullable().optional(),
  // Display hint for a FUTURE freeze ("trading pauses at …"), only meaningful while status is open
  // AND freezeAt is in the future. NEVER an input to whether the market is closed — an open status
  // can carry a past freezeAt after the pick deadline; closed-ness comes from status/reason only.
  freezeAt: z.string().optional(),
  pickDeadline: z.string().optional(),
  reason: z.string().optional(),
  reopensAt: z.string().optional(),
  // Backend-owned one-shot scheduling hint. Never infer marketplace availability from this time.
  refreshAt: z.string().nullable().optional(),
});
export type MarketplaceStatus = z.infer<typeof MarketplaceStatusSchema>;

const PublicListingBaseContestSchema = z.object({
  id: IdSchema,
  name: z.string(),
  prizePoolCents: CentsSchema,
  prizePoolDollars: z.number(),
  totalEntries: z.number().int().nonnegative(),
  contestType: z.string(),
  // Adds the contest thumbnail to offer/listing contests.
  thumbnailUrl: z.string().nullable().optional(),
  // Required: null means "no current slate" (that is the contract); an absent field is a
  // contract violation and fails like any other required field.
  currentSlate: PublicCurrentSlateSchema.nullable(),
  // Reuse limit: reuses beyond the first pick — 0 = each team once, N = usable N+1 times,
  // null = unlimited. Required. Modeled as number (not .int()) to accept forward evolution.
  pickReuseLimit: z.number().nullable(),
  // The contest's marketplace status on listing reads (listing / detail / offers / buy-now), so the
  // listing surfaces gate off it instead of expiration-constraints.
  // Wire-optional + nullable: absent and null both degrade to open / no gate.
  marketplaceStatus: MarketplaceStatusSchema.optional().nullable(),
});

const PublicListingSurvivorContestSchema = PublicListingBaseContestSchema.extend({
  contestType: z.enum(['team_survivor', 'player_survivor']),
  aliveEntries: z.number().int().nonnegative(),
});

// Survivor first: its narrower contestType enum must win before the base's plain string.
const PublicListingContestSchema = z.union([
  PublicListingSurvivorContestSchema,
  PublicListingBaseContestSchema,
]);

// The FE branches on listing status; the known set is exported for those branches, but
// the schema is forward-lenient so a new terminal status never DROPS the row. Unknown
// values fall through every `=== known` branch.
export const LISTING_STATUSES = [
  'active',
  'pending_settlement',
  'filled',
  'cancelled',
  'expired',
] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];
const ListingStatusSchema = z.string();

// Display-only, forward-lenient. Known: user_cancelled | admin_cancelled |
// listing_cancelled | marketplace_frozen | contest_closed | entry_eliminated |
// insufficient_bps | entry_cancelled.
const ListingCancellationReasonSchema = z.string();

const OFFER_PICK_CONTROL_FILTERS = ['any', 'with_pick_control', 'without_pick_control'] as const;
const OfferPickControlFilterSchema = z.enum(OFFER_PICK_CONTROL_FILTERS);

const SellerMinimumSchema = z.object({
  estimatedValuePercent: z.number(),
  estimatedValueCents: CentsSchema,
  estimatedValueDollars: z.number(),
  minPriceCents: CentsSchema,
  minPriceDollars: z.number(),
});

const OfferableShareSchema = z.object({
  shareBps: BasisPointsSchema,
  conveysPickControl: z.boolean(),
  allowed: z.boolean(),
  minPriceCents: CentsSchema.nullable(),
  minPriceDollars: z.number().nullable(),
});

const OfferTermsShape = {
  acceptsOffers: z.boolean().optional().catch(undefined),
  sellerMinimum: SellerMinimumSchema.nullable().optional().catch(undefined),
  pickControlRequirement: OfferPickControlFilterSchema.optional().catch(undefined),
  offerableShares: z.array(OfferableShareSchema).optional().catch(undefined),
};

export const PublicListingSchema = z.object({
  id: IdSchema,
  entry: PublicListingEntrySchema,
  contest: PublicListingContestSchema,
  picks: z.array(PublicListingPickSchema),
  league: z.string(),
  slateId: IdSchema,
  seller: MarketplaceUserSchema,
  shareBpsOffered: BasisPointsSchema,
  askingPriceCents: CentsSchema,
  askingPriceDollars: z.number(),
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
  priority: z.number().int().nullable(),
  status: ListingStatusSchema,
  cancellationReason: ListingCancellationReasonSchema.nullable(),
  cancellationDesc: z.string().nullable(),
  expiresAt: IsoDateSchema,
  createdAt: IsoDateSchema,
  // Viewer-independent (cache-safe); conveyance derives client-side from this plus the
  // viewer's share vs MIN_MAJORITY_BPS.
  sellerHasPickControl: z.boolean(),
  groupEntry: z.boolean().optional().catch(undefined),
});

// handle is sentinel-degraded ('unknown'); exactly one element has isPickController
// true. The FE derives the viewer's own row by matching userId — the server ships no
// isViewer flag.
const ShareholderSchema = z.object({
  userId: IdSchema,
  shareBps: BasisPointsSchema,
  handle: z.string(),
  avatarUrl: z.string().nullable(),
  isPickController: z.boolean(),
});

export const PublicListingDetailSchema = PublicListingSchema.extend({
  offersCount: z.number().int().nonnegative(),
  viewerIsGroupMember: z.literal(true).optional().catch(undefined),
  // Required-nullable: null when anonymous. Present only on GET /listings/:id.
  viewerContext: z
    .object({
      shareBps: BasisPointsSchema,
      wouldConveyPickControl: z.boolean(),
    })
    .nullable(),
  // Enrichment facets degraded on this detail. `entry_ownership` forces the viewer's
  // share to 0 server-side, so wouldConveyPickControl can understate — conveyance then
  // renders unknown (utils/degradation.isEntryOwnershipDegraded). Lenient: absent = none.
  degraded: z.array(z.string()).optional(),
  ...OfferTermsShape,
  entry: PublicListingEntrySchema.extend({
    shareholders: z
      .array(
        ShareholderSchema.omit({ avatarUrl: true }).partial({
          handle: true,
          isPickController: true,
        })
      )
      .optional(),
    // Detail-only (GET /listings/:id): the buyer panel's Available-teams section reads these.
    // usedTeamIds = the entry's revealed picks; availableTeamIds = universe teams within the reuse
    // limit. Both always present (empty when no revealed picks / the team universe is degraded).
    usedTeamIds: z.array(z.string()),
    availableTeamIds: z.array(z.string()),
  }),
});

// `userId`, not `id`. Identity sentinel 'unknown' on an identity_enrichment miss
// (rendered as the neutral "Unknown").
const SettlementBuyerSchema = z.object({
  userId: IdSchema,
  handle: z.string(),
  avatarUrl: z.string().nullable(),
});

// The completed sale keyed to a filled listing. salePriceCents is the gross
// (counter price when countered); sellerFeeCents / buyerFeeCents are the settled fees;
// shareBps is the share that actually sold; remainingShareBps is the seller's live
// remainder (0 = sold out). saleType is forward-lenient (known: accepted_offer | buy_now).
const SettlementSummarySchema = z.object({
  transactionId: IdSchema,
  buyer: SettlementBuyerSchema,
  soldAt: IsoDateSchema,
  salePriceCents: CentsSchema,
  sellerFeeCents: CentsSchema,
  buyerFeeCents: CentsSchema,
  // Adds dollar counterparts alongside the cents fields (the *Dollars convention on the rest of
  // the schema). The FE still computes from cents; nothing reads these yet.
  salePriceDollars: z.number().optional(),
  sellerFeeDollars: z.number().optional(),
  buyerFeeDollars: z.number().optional(),
  shareBps: BasisPointsSchema,
  saleType: z.string(),
  remainingShareBps: BasisPointsSchema,
  pickControlTransferred: z.boolean(),
});

// GET /my-listings rows: the public listing minus picks (cancellation fields are
// already on the base). The offer count is derived from the live received-offers inbox,
// not a denormalized row field.
export const MyListingSchema = PublicListingSchema.omit({ picks: true }).extend({
  // A filled listing with exactly one completed transaction ships
  // its settlement summary; null for unfilled / legacy multi-transaction / read-failure
  // rows (never a partial object). Required-nullable on the wire; kept optional here so a
  // forward-evolved row that omits it still parses (never dropped) and reads as "no sale".
  settlement: SettlementSummarySchema.nullable().optional(),
  // Live offers on this listing (pending, not past expiry). Optional here so a row from a
  // backend without it still parses; absent reads as no badge.
  pendingOfferCount: z.number().optional(),
});

// POST /listings body; expiresAt omitted = the backend defaults to pick deadline
// minus its freeze buffer.
export const CreateListingRequestSchema = z.object({
  entryId: IdSchema,
  contestId: IdSchema,
  shareBps: BasisPointsSchema,
  askingPriceCents: CentsSchema,
  expiresAt: IsoDateSchema.optional(),
});

export const CreatedListingSchema = PublicListingSchema.omit({ picks: true });

// GET /contests/:contestId/expiration-constraints — the create/edit flows' expiry
// bounds plus the only shipped source of the contest pick deadline.
// Nested discriminated union mirroring the backend: branch
// on `state`, then on `reason` inside closed. `sellerFeeRateBps`/`buyerFeeRateBps` are
// required on every branch — this endpoint is the required source of both fee rates for
// the create/edit flows' fee math.
const OpenExpirationConstraintsSchema = z.object({
  state: z.literal('open'),
  contestId: IdSchema,
  pickDeadline: IsoDateSchema,
  bufferMinutes: z.number().int().nonnegative(),
  earliestExpirationTime: IsoDateSchema,
  latestExpirationTime: IsoDateSchema,
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
});

const FrozenExpirationConstraintsSchema = z.object({
  state: z.literal('closed'),
  reason: z.literal('frozen'),
  contestId: IdSchema,
  reopensAt: IsoDateSchema,
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
});

const NoUpcomingSlateExpirationConstraintsSchema = z.object({
  state: z.literal('closed'),
  reason: z.literal('no_upcoming_slate'),
  contestId: IdSchema,
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
});

// A non-active contest and a not-enabled contest — both closed, no time fields; the
// surfaces gate off marketplaceStatus, so these carry no expiry values.
const ContestClosedExpirationConstraintsSchema = z.object({
  state: z.literal('closed'),
  reason: z.literal('contest_closed'),
  contestId: IdSchema,
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
});

const NotEnabledExpirationConstraintsSchema = z.object({
  state: z.literal('closed'),
  reason: z.literal('not_enabled'),
  contestId: IdSchema,
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
});

// z.union, NOT discriminatedUnion: the two closed variants share state 'closed', and this
// zod version rejects a nested discriminatedUnion('reason', …) as an option of an outer
// discriminatedUnion('state', …) (it wants a ZodObject per option) — it fails to compile.
// The distinct state/reason literals still narrow cleanly for consumers, so leave it as a
// union. Do not "fix" it back to discriminatedUnion.
export const ExpirationConstraintsSchema = z.union([
  OpenExpirationConstraintsSchema,
  FrozenExpirationConstraintsSchema,
  NoUpcomingSlateExpirationConstraintsSchema,
  ContestClosedExpirationConstraintsSchema,
  NotEnabledExpirationConstraintsSchema,
]);

export const ContestTopListingSchema = z.object({
  id: IdSchema,
  seller: MarketplaceUserSchema,
  sellerHasPickControl: z.boolean(),
  shareBpsOffered: BasisPointsSchema,
  askingPriceCents: CentsSchema,
});

export const ContestBrowseRowSchema = z.object({
  contestId: IdSchema,
  name: z.string(),
  contestType: z.string(),
  league: z.string().nullable(),
  // Display-only, forward-lenient (a different enum from listing status). Known: active |
  // closed | finalized | cancelled.
  status: z.string(),
  prizePoolCents: CentsSchema,
  totalEntries: z.number().int().nonnegative(),
  openListingsCount: z.number().int().nonnegative(),
  lowestAskCents: CentsSchema.nullable(),
  lowestAskListingId: IdSchema.nullable(),
  // null = valuation unavailable; ZERO is a real zero.
  aliveEntries: z.number().int().nonnegative().nullable(),
  // Always present; sentinel-degraded (utils/degradation) — the byline omits a sentinel
  // handle rather than rendering 'unknown'.
  commissioner: CommissionerRefSchema,
  bannerUrl: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable(),
  currentSlate: PublicCurrentSlateSchema.nullable(),
  // See PublicListingBaseContestSchema — fail-closed the same way.
  pickReuseLimit: z.number().nullable(),
  // Contest-level marketplace status, same object as the entry-view. Wire-optional + nullable:
  // both absent and null degrade to trade-on / no banner.
  marketplaceStatus: MarketplaceStatusSchema.optional().nullable(),
  topListings: z.array(ContestTopListingSchema).optional(),
});

const ContestEntryListingSummarySchema = z.object({
  listingId: IdSchema,
  shareBpsOffered: BasisPointsSchema,
  askingPriceCents: CentsSchema,
  expiresAt: IsoDateSchema,
  // The All-entries table shows the listing on the seller's own ownership row (matching this
  // against shareholder.userId), not the pick controller's — the two are not always the same owner.
  sellerUserId: IdSchema,
  groupEntry: z.boolean().optional().catch(undefined),
});

export const ContestEntryRowSchema = z.object({
  entryId: IdSchema,
  contestId: IdSchema,
  order: z.number().int(),
  livesRemaining: z.number().int(),
  listing: ContestEntryListingSummarySchema.nullable(),
  // Required non-null; sentinel-degraded handle ('unknown'). The owner column renders THIS.
  pickController: z.object({
    userId: IdSchema,
    shareBps: BasisPointsSchema,
    handle: z.string(),
    avatarUrl: z.string().nullable(),
  }),
  // The All-entries table renders one row per shareholder. A cold (never-split) entry is
  // a single synthetic shareholder at 10000 = the pick controller.
  shareholders: z.array(ShareholderSchema),
  // usedTeamIds = the entry's revealed picks; availableTeamIds = universe teams not over the reuse
  // limit. Independent axes: a team can be used and still available (reuse limit >= 1). Both are
  // always present (empty when the entry has no revealed picks / the team universe is degraded).
  usedTeamIds: z.array(z.string()),
  availableTeamIds: z.array(z.string()),
});

// A team in the contest's team universe from GET /contests/:id/teams. Logos stay FE-owned (CDN by
// league alias + teamId), never on the wire.
export const ContestTeamSchema = z.object({
  teamId: IdSchema,
  teamName: z.string(),
  teamAlias: z.string(),
});

// GET /contests/:id/teams envelope: the full team universe plus a degraded facet list ('team_universe'
// when the universe is unavailable and teams is empty). Kept forward-lenient (z.string()) so a new
// facet never fails the parse.
export const ContestTeamsResponseSchema = z.object({
  teams: z.array(ContestTeamSchema),
  degraded: z.array(z.string()),
});

// The summary shape /my-portfolio embeds; the editable My-listings rows still come from
// the full MyListing read.
const MyPortfolioActiveListingSchema = z.object({
  id: IdSchema,
  shareBpsOffered: BasisPointsSchema,
  askingPriceCents: CentsSchema,
  askingPriceDollars: z.number(),
  expiresAt: IsoDateSchema,
  sellerHasPickControl: z.boolean(),
});

// The portfolio entry ref (/my-portfolio): name + number replace the listing entry's
// `order`. Rows label as the entry name when set, else "Entry {number}". `status` is nullable —
// the contest-summary source degrades to null; Holdings combines it with livesRemaining when
// determining whether the entry has been eliminated.
const MyPortfolioEntrySchema = z.object({
  id: IdSchema,
  name: z.string().nullable(),
  number: z.number().int(),
  status: z.string().nullable(),
  livesRemaining: z.number().int(),
});

export const PortfolioHoldingSchema = z.object({
  entryId: IdSchema,
  contestId: IdSchema,
  shareBps: BasisPointsSchema,
  // shareBps minus the bps reserved on active listings — the server computes it, so the FE never
  // derives "reserved" from the listings array.
  availableBps: BasisPointsSchema,
  // Display-only, forward-lenient. Known: original | marketplace_purchase | direct_offer
  // | transfer | split.
  acquiredVia: z.string(),
  // Null together (expect null for `original` holdings); the cost column renders "—".
  costBasisCents: CentsSchema.nullable(),
  costBasisDollars: z.number().nullable(),
  // On an enrichment miss it ships fixed sentinels — contestType
  // 'unknown' is the miss signal (utils/degradation.isDegradedHoldingContest), never the
  // name string or zeroed counts. Rows identify as "Contest · Entry #order".
  contest: z.object({
    id: IdSchema,
    name: z.string(),
    contestType: z.string(),
    league: z.string().nullable().optional(),
    prizePoolCents: CentsSchema,
    prizePoolDollars: z.number(),
    totalEntries: z.number().int().nonnegative(),
    thumbnailUrl: z.string().nullable(),
    aliveEntries: z.number().int().nonnegative().nullable(),
  }),
  // name null → the row falls back to "Entry {number}".
  entry: MyPortfolioEntrySchema,
  // Authoritative even when ownership is degraded (resolved from the fallback pick
  // controller while shareholders is []); never derive this from the shareholders array.
  controlsPicks: z.boolean(),
  // The entry's full ownership breakdown — all-or-nothing: the complete table, or [] when
  // ownership is degraded (the response carries degraded: ['entry_ownership'] then). A cold
  // (never-split) entry is one synthetic shareholder at 10000 = the pick controller.
  shareholders: z.array(ShareholderSchema),
  activeListings: z.array(MyPortfolioActiveListingSchema),
  // Categories pending and countered only.
  pendingOffersReceived: z.number().int().nonnegative(),
  groupSale: z
    .object({
      agreedCount: z.number().int().nonnegative(),
      memberCount: z.number().int().nonnegative(),
      listed: z.boolean(),
      listingId: IdSchema.optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
});

const paginationShape = {
  offset: z.number(),
  limit: z.number(),
  total: z.number(),
};

// List envelopes keep rows as unknown so one unparseable row is dropped, not the page.
const paginatedUnknownSchema = z.object({ ...paginationShape, data: z.array(z.unknown()) });

// Response-level `degraded` facet on the listing surfaces (team_universe = a row's availableTeamIds
// is empty because the team universe was unavailable). Optional/lenient — parses before and after
// the producer sends it, and never a strict enum on a wire-optional array (forward evolution).
export const PaginatedListingsEnvelopeSchema = paginatedUnknownSchema.extend({
  degraded: z.array(z.string()).optional(),
});

export const PaginatedMyListingsEnvelopeSchema = paginatedUnknownSchema;

// Adds a response-level `degraded` facet array to the offer reads.
export const PaginatedOffersEnvelopeSchema = paginatedUnknownSchema.extend({
  degraded: z.array(z.string()).optional(),
});

// `total` is a lower bound from the discovery upstream — drive paging with `hasMore`.
export const ContestBrowseEnvelopeSchema = paginatedUnknownSchema.extend({
  hasMore: z.boolean(),
  degraded: z.array(z.string()),
});

const SaleParticipantSchema = z.object({
  userId: IdSchema,
  handle: z.string(),
  avatarUrl: z.string().nullable(),
});

const SalePickSchema = z.object({
  teamId: IdSchema,
  teamName: z.string(),
  teamAlias: z.string(),
  iconUrl: z.string().nullable().optional(),
  gameId: IdSchema,
  slateId: IdSchema,
  slateName: z.string().nullable(),
});

const SaleSlateSchema = z.object({
  id: IdSchema,
  name: z.string().nullable(),
});

export const ContestSaleSchema = z.object({
  transactionId: IdSchema,
  entryId: IdSchema,
  soldAt: IsoDateSchema,
  shareBps: BasisPointsSchema,
  sharePercent: z.number(),
  salePriceCents: CentsSchema,
  salePriceDollars: z.number(),
  pricePerPercentCents: CentsSchema,
  pricePerPercentDollars: z.number(),
  estimatedValueCents: CentsSchema.nullable().optional().catch(null),
  estimatedValueDollars: z.number().nullable().optional().catch(null),
  pickControlTransferred: z.boolean().optional(),
  groupEntry: z.literal(true).optional().catch(undefined),
  seller: SaleParticipantSchema,
  buyer: SaleParticipantSchema,
  hasSnapshot: z.boolean(),
  picks: z.array(SalePickSchema).nullable().catch(null),
  slate: SaleSlateSchema.nullable().catch(null),
});

const SaleTeamSchema = z.object({
  teamId: IdSchema,
  teamName: z.string(),
  teamAlias: z.string(),
  iconUrl: z.string().nullable().optional(),
});

export const ContestSaleDetailSchema = ContestSaleSchema.extend({
  buyerFeeCents: CentsSchema.nullable().catch(null),
  buyerFeeDollars: z.number().nullable().catch(null),
  availableTeams: z.array(SaleTeamSchema).nullable().catch(null),
  degraded: z.array(z.string()).catch([]),
});

export const ContestSalesEnvelopeSchema = z.object({
  data: z.array(z.unknown()),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  degraded: z.array(z.string()),
});

export const RecentSaleSchema = z.object({
  transactionId: IdSchema,
  entryId: IdSchema,
  contestId: IdSchema,
  contestName: z.string().nullable(),
  soldAt: IsoDateSchema,
  shareBps: BasisPointsSchema,
  sharePercent: z.number(),
  salePriceCents: CentsSchema,
  salePriceDollars: z.number(),
  pickControlTransferred: z.boolean().optional(),
  groupEntry: z.literal(true).optional().catch(undefined),
  seller: SaleParticipantSchema,
  buyer: SaleParticipantSchema,
});

export const RecentSalesEnvelopeSchema = z.object({
  data: z.array(z.unknown()),
  degraded: z.array(z.string()).catch([]),
});

export const ContestSalesStatsSchema = z.object({
  totalSales: z.number().int().nonnegative(),
  salesLast7Days: z.number().int().nonnegative(),
  salesPrevious7Days: z.number().int().nonnegative(),
  salesTrendPercent: z.number().nullable(),
  medianPricePerPercentCents: CentsSchema.nullable(),
  medianPricePerPercentDollars: z.number().nullable(),
  previousMedianPricePerPercentCents: CentsSchema.nullable(),
  previousMedianPricePerPercentDollars: z.number().nullable(),
  medianTrendPercent: z.number().nullable(),
  volumeCents: CentsSchema,
  volumeDollars: z.number(),
});

export const ContestAliveStatsSchema = z.object({
  aliveEntries: z.number().int().nonnegative().nullable(),
  totalEntries: z.number().int().nonnegative(),
  previousSlateAliveEntries: z.number().int().nonnegative().nullable(),
  aliveTrendPercent: z.number().nullable(),
});

export const ContestSummarySchema = ContestBrowseRowSchema.extend({
  salesStats: ContestSalesStatsSchema.nullable().optional().catch(null),
  aliveStats: ContestAliveStatsSchema.nullable().optional().catch(null),
});

export const ContestEntriesEnvelopeSchema = paginatedUnknownSchema.extend({
  hasMore: z.boolean(),
  degraded: z.array(z.string()),
  // Contest-wide count of ownership rows across all alive entries (the All-entries footer counts
  // ownerships, not entries); null when the ownership sweep degraded ('ownership_total').
  totalOwnerships: z.number().nullable(),
});

export const PortfolioEnvelopeSchema = paginatedUnknownSchema.extend({
  // Response-level degradation facets (entry_ownership forces controlsPicks false response-wide, so
  // the pick-control column reads unknown). Optional and forward-lenient: captured fixtures predate
  // the field, and an unknown facet string still parses.
  degraded: z.array(z.string()).optional(),
});

// The FE branches on a couple of offer statuses (pending vs terminal) and renders a label
// for each; the known set is exported for those, but the schema is forward-lenient so a
// new terminal status (e.g. a competing offer closed by a fill) never DROPS the offer row.
// The label renders a humanized fallback.
export const OFFER_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'expired',
  'cancelled',
  'failed',
] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];
const OfferStatusSchema = z.string();

export const FUNDING_TREATMENTS = ['escrow', 'buy_now', 'offer_cap'] as const;
export const FundingTreatmentSchema = z.enum(FUNDING_TREATMENTS);
export type FundingTreatment = z.infer<typeof FundingTreatmentSchema>;

export const PublicOfferSchema = z.object({
  id: IdSchema,
  // null for direct offers.
  listingId: IdSchema.nullable(),
  entry: PublicListingEntrySchema,
  contest: PublicListingContestSchema,
  buyer: MarketplaceUserSchema,
  target: MarketplaceUserSchema,
  shareBpsRequested: BasisPointsSchema,
  bidPriceCents: CentsSchema,
  bidPriceDollars: z.number(),
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
  isCounter: z.boolean(),
  counterBidCents: CentsSchema.nullable(),
  counterBidDollars: z.number().nullable(),
  // null = share not countered; the accepted share is `counterShareBps ?? shareBpsRequested`.
  counterShareBps: BasisPointsSchema.nullable(),
  // Server-computed: true when accepting THIS offer conveys pick control (seller controls picks
  // AND the buyer ends ≥ majority with the effective share). The FE never derives the majority
  // formula.
  conveysPickControl: z.boolean().optional(),
  escrowHoldCents: CentsSchema.nullable(),
  escrowReleased: z.boolean(),
  fundingTreatment: FundingTreatmentSchema.optional().catch(undefined),
  status: OfferStatusSchema,
  // Why a 'cancelled' offer closed — terminal 'listing_filled' when a competing offer
  // was closed by the listing selling. Forward-lenient string (unknown reasons render the
  // plain status); nullable/optional so a response without the field never drops the offer row.
  cancellationReason: z.string().nullable().optional(),
  expiresAt: IsoDateSchema,
  createdAt: IsoDateSchema,
  // The buyer's current holding on the entry, for the counter/accept pick-control preview: 0 = holds
  // nothing (a real read), null = ownership could not be read. Optional so an older producer (this
  // ships ahead of the backend) still parses; MyOfferListItem and the entry-view offer arrays inherit
  // it from here.
  buyerHeldBps: z.number().int().nullable().optional(),
  bidPercentOfEv: z.number().nullable().optional().catch(null),
  notificationSuppression: z
    .discriminatedUnion('reason', [
      z.object({
        reason: z.literal('below_notification_threshold'),
        thresholdEstimatedValuePercent: z.number(),
      }),
      z.object({ reason: z.literal('notifications_off') }),
    ])
    .nullable()
    .optional()
    .catch(null),
});

// GET /my-offers: the categorised, paginated Offers list. `category` orders the list and
// drives the section headers.
export const OFFER_CATEGORIES = ['pending', 'countered', 'closed'] as const;
export const OfferCategorySchema = z.enum(OFFER_CATEGORIES);

// The list item reuses the PublicOffer DTO plus `category`; the entry gains an optional name +
// number (the Selling table shows the entry's name, falling back to its number/order). Optional
// so a lagging response never drops the row (forward-lenient).
const MyOfferEntrySchema = PublicListingEntrySchema.extend({
  name: z.string().optional(),
  number: z.number().int().optional(),
});
export const MyOfferListItemSchema = PublicOfferSchema.extend({
  entry: MyOfferEntrySchema,
  category: OfferCategorySchema,
});

export const MyOffersTotalsSchema = z.object({
  pending: z.number().int().nonnegative(),
  countered: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
});

export const MyOffersListEnvelopeSchema = z.object({
  // Rows are parsed per-row by getMyOffersList's parseRows so one malformed offer never
  // blanks the rest of the (otherwise valid) list.
  data: z.array(z.unknown()),
  total: z.number().int().nonnegative(),
  // Per-category counts for the section headers and the nav badge; independent of the loaded page.
  totals: MyOffersTotalsSchema,
  limit: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  degraded: z.array(z.string()).optional(),
});

// GET /entries/:id: one viewer-aware read backing the direct-offer buy panel + the
// entry-based selling panel — no per-panel side endpoints. Forward-lenient: enrichment refs
// degrade, and bestOffer is kept opaque (unused here).
// The holder's entry label is viewer.entryName; the entry ref itself carries no name.
export const EntryViewSchema = z.object({
  entry: z.object({
    id: IdSchema,
    order: z.number().int(),
    status: z.string().optional(),
    livesRemaining: z.number().int(),
  }),
  contest: z.object({
    id: IdSchema,
    name: z.string(),
    contestType: z.string().optional(),
    // League alias for the team-logo CDN lookup.
    league: z.string().nullable().optional(),
    thumbnailUrl: z.string().nullable().optional(),
    prizePoolCents: CentsSchema,
    totalEntries: z.number().int().nonnegative().optional(),
    aliveEntries: z.number().int().nonnegative().nullable().optional(),
    marketplaceStatus: MarketplaceStatusSchema.optional(),
    // Drives the direct-offer Pick-history empty state (hidden-until-lock vs generic). Required and
    // nullable, matching the listing sibling: the producer returns it on every entry-view, null where
    // no slate is active.
    currentSlate: PublicCurrentSlateSchema.nullable(),
  }),
  ownership: z.object({
    shareholders: z.array(ShareholderSchema),
    pickController: z.object({ userId: IdSchema, shareBps: BasisPointsSchema }).nullable(),
  }),
  picks: z.object({
    // The entry's own picks carry team names/aliases, so pick labels need no league lookup;
    // league still rides on `contest` for the team-logo CDN.
    picks: z.array(PublicListingPickSchema),
    usedTeamIds: z.array(IdSchema),
    availableTeamIds: z.array(IdSchema),
  }),
  // Every open listing on the entry (all sellers; empty when none), each the FULL listing DTO
  // (identical to a /my-listings row) — the Selling panel filters this to the viewer's own rows for
  // Edit / Cancel / Relist, and the Listing-detail "Other listings" tab reads the rest.
  activeListings: z.array(MyListingSchema),
  bestOffer: z.unknown().nullable().optional(),
  viewer: z
    .object({
      // The holder's label for their own entry; null when the enrichment is unavailable.
      entryName: z.string().nullable().optional(),
      shareBps: BasisPointsSchema,
      controlsPicks: z.boolean(),
      acquiredVia: z.string(),
      costBasisCents: CentsSchema.nullable(),
      availableBps: BasisPointsSchema,
    })
    .nullable(),
  offers: z.object({
    asBuyer: z.array(MyOfferListItemSchema),
    asTarget: z.array(MyOfferListItemSchema),
  }),
  degraded: z.array(z.string()).optional(),
  ...OfferTermsShape,
  offerTargetUserId: IdSchema.nullable().optional().catch(undefined),
});

const GroupSaleEstimateSchema = z.object({
  basis: z.string(),
  salePriceCents: CentsSchema,
  salePriceDollars: z.number().nonnegative(),
  grossCents: CentsSchema,
  grossDollars: z.number().nonnegative(),
  sellerFeeCents: CentsSchema,
  sellerFeeDollars: z.number().nonnegative(),
  netCents: CentsSchema,
  netDollars: z.number().nonnegative(),
});

const GroupSaleListingViewSchema = z.object({
  id: IdSchema,
  status: ListingStatusSchema,
  askingPriceCents: CentsSchema,
  askingPriceDollars: z.number().nonnegative(),
  expiresAt: IsoDateSchema,
});

export const GroupSaleViewSchema = z.object({
  entryId: IdSchema,
  contestId: IdSchema,
  splitGroupId: IdSchema.nullable(),
  pickController: z.object({ userId: IdSchema, handle: z.string() }),
  members: z.array(
    z.object({
      userId: IdSchema,
      shareBps: z.number().int().min(1).max(10_000),
      consent: z.object({
        status: z.enum(['agreed', 'waiting']),
        agreedAt: IsoDateSchema.nullable(),
      }),
      name: z.string(),
      handle: z.string(),
      avatarUrl: z.string().nullable(),
      sharePercent: z.number().min(0).max(100),
      isPickController: z.boolean(),
      isViewer: z.boolean(),
    })
  ),
  viewer: z.object({
    shareBps: z.number().int().min(1).max(10_000),
    sharePercent: z.number().min(0).max(100),
    estimate: GroupSaleEstimateSchema.nullable(),
  }),
  listing: GroupSaleListingViewSchema.nullable(),
  canList: z.boolean(),
  agreedCount: z.number().int().nonnegative(),
  memberCount: z.number().int().nonnegative(),
  waitingOnCount: z.number().int().nonnegative(),
  state: z
    .enum(['not_group_entry', 'unavailable', 'collecting', 'ready', 'listed', 'settling', 'sold'])
    .catch('unavailable'),
  reason: z
    .enum([
      'disabled',
      'market_closed',
      'restricted',
      'invites_pending',
      'entry_eliminated',
      'member_ineligible',
    ])
    .nullable()
    .catch(null),
});
export type GroupSaleView = z.infer<typeof GroupSaleViewSchema>;

export const CreateGroupSaleListingRequestSchema = z.object({
  askingPriceCents: CentsSchema,
  expiresAt: IsoDateSchema,
});
export type CreateGroupSaleListingRequest = z.infer<typeof CreateGroupSaleListingRequestSchema>;

// PATCH /offers/:offerId/accept — the settlement engine's committed result.
export const SettlementResultSchema = z.object({
  offerId: IdSchema,
  transactionId: IdSchema,
  entryId: IdSchema,
  ownershipVersion: z.number(),
  occurredAt: IsoDateSchema,
});

// POST /listings/:id/buy-now — an accepted offer settling the purchase.
export const BuyNowResultSchema = z.object({
  id: IdSchema,
  status: z.literal('accepted'),
  transactionId: IdSchema,
  entry: PublicListingEntrySchema,
  contest: PublicListingContestSchema,
  buyer: MarketplaceUserSchema,
  targetUser: MarketplaceUserSchema,
  shareBpsRequested: BasisPointsSchema,
  bidPriceCents: CentsSchema,
  bidPriceDollars: z.number(),
  createdAt: IsoDateSchema,
});

export type MarketplaceUser = z.infer<typeof MarketplaceUserSchema>;
export type BuyNowResult = z.infer<typeof BuyNowResultSchema>;
export type PublicListing = z.infer<typeof PublicListingSchema>;
export type PublicListingDetail = z.infer<typeof PublicListingDetailSchema>;
// Shared pick-history sub-shapes: a revealed pick row and the contest's current slate. Both the
// listing detail and the entry view carry these, so PicksBody types its props off them directly
// rather than off either panel's whole DTO.
export type PublicListingPick = z.infer<typeof PublicListingPickSchema>;
export type PublicCurrentSlate = z.infer<typeof PublicCurrentSlateSchema>;
export type MyListing = z.infer<typeof MyListingSchema>;
export type CreateListingRequest = z.infer<typeof CreateListingRequestSchema>;
export type CreatedListing = z.infer<typeof CreatedListingSchema>;
export type ExpirationConstraints = z.infer<typeof ExpirationConstraintsSchema>;

export const MarketplaceFeeTierSchema = z.object({
  thresholdCents: CentsSchema,
  sellerFeeRateBps: BasisPointsSchema,
  buyerFeeRateBps: BasisPointsSchema,
});
export const MarketplaceFeeTiersSchema = z.object({
  baseSellerFeeRateBps: BasisPointsSchema,
  baseBuyerFeeRateBps: BasisPointsSchema,
  tiers: z.array(MarketplaceFeeTierSchema),
});
export type MarketplaceFeeTier = z.infer<typeof MarketplaceFeeTierSchema>;
export type MarketplaceFeeTiers = z.infer<typeof MarketplaceFeeTiersSchema>;

export type SettlementResult = z.infer<typeof SettlementResultSchema>;
export type ContestBrowseRow = z.infer<typeof ContestBrowseRowSchema>;
export type ContestTopListing = z.infer<typeof ContestTopListingSchema>;
export type ContestEntryRow = z.infer<typeof ContestEntryRowSchema>;
export type ContestSale = z.infer<typeof ContestSaleSchema>;
export type ContestSaleDetail = z.infer<typeof ContestSaleDetailSchema>;
export type RecentSale = z.infer<typeof RecentSaleSchema>;
export type ContestSalesStats = z.infer<typeof ContestSalesStatsSchema>;
export type ContestAliveStats = z.infer<typeof ContestAliveStatsSchema>;
export type ContestSummary = z.infer<typeof ContestSummarySchema>;
export type Shareholder = z.infer<typeof ShareholderSchema>;
export type ContestTeam = z.infer<typeof ContestTeamSchema>;
export type ContestTeamsResponse = z.infer<typeof ContestTeamsResponseSchema>;
export type PortfolioHolding = z.infer<typeof PortfolioHoldingSchema>;
export type PublicOffer = z.infer<typeof PublicOfferSchema>;
export type OfferCategory = z.infer<typeof OfferCategorySchema>;
export type EntryView = z.infer<typeof EntryViewSchema>;
export type MyOfferListItem = z.infer<typeof MyOfferListItemSchema>;
export type MyOffersListEnvelope = z.infer<typeof MyOffersListEnvelopeSchema>;

export type GroupViewUser = z.infer<typeof GroupViewUserSchema>;
export type GroupInvite = z.infer<typeof GroupInviteSchema>;
export type GroupInviteStatus = GroupInvite['status'];
export type GroupInviteWithContest = z.infer<typeof GroupInviteWithContestSchema>;
export type GroupEntryShareholder = z.infer<typeof GroupEntryShareholderSchema>;
export type GroupEntryPendingInvitee = z.infer<typeof GroupEntryPendingInviteeSchema>;
export type GroupEntryView = z.infer<typeof GroupEntryViewSchema>;
export type CreateGroupInvitesRequest = z.infer<typeof CreateGroupInvitesRequestSchema>;

export const OffersCountEnvelopeSchema = z.object({
  total: z.number().int().nonnegative(),
});

export const CONTEST_OFFER_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'expired',
  'cancelled',
  'failed',
  'unknown',
] as const;
export const ContestOfferStatusSchema = z.enum(CONTEST_OFFER_STATUSES).catch('unknown');
export type ContestOfferStatus = (typeof CONTEST_OFFER_STATUSES)[number];

const ContestOfferEntrySchema = z.object({
  id: IdSchema,
  name: z.string().nullable().optional(),
  order: z.number().int().nullable().optional(),
});

export const MyOfferSchema = z.object({
  id: IdSchema,
  category: OfferCategorySchema,
  entry: ContestOfferEntrySchema,
  buyer: MarketplaceUserSchema,
  target: MarketplaceUserSchema.optional(),
  isCounter: z.boolean().optional(),
  shareBpsRequested: BasisPointsSchema,
  bidPriceDollars: z.number(),
  counterShareBps: BasisPointsSchema.nullable().optional(),
  counterBidDollars: z.number().nullable().optional(),
  conveysPickControl: z.boolean().nullable().optional(),
  status: ContestOfferStatusSchema,
  expiresAt: IsoDateSchema,
  createdAt: IsoDateSchema.optional(),
});

export type MyOffer = z.infer<typeof MyOfferSchema>;

export const MyOffersEnvelopeSchema = paginatedUnknownSchema.extend({
  totals: MyOffersTotalsSchema.catch({ pending: 0, countered: 0, closed: 0 }),
});

export const OFFER_FEED_TYPES = [
  'offer_received',
  'counter_received',
  'offer_accepted',
  'offer_declined',
  'offer_cancelled',
  'offer_expired',
  'offer_failed',
] as const;
export type OfferFeedType = (typeof OFFER_FEED_TYPES)[number];

const OfferFeedActorSchema = z.object({
  userId: IdSchema.optional(),
  handle: z.string(),
  avatarUrl: z.string().nullable().optional(),
});

const OfferFeedCommissionerSchema = z.object({
  id: IdSchema.nullable().optional(),
  handle: z.string(),
  avatarUrl: z.string().nullable().optional(),
});

const OfferFeedContestSchema = z.object({
  id: IdSchema,
  name: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
});

export const OfferFeedItemSchema = z.object({
  offerId: IdSchema,
  type: z.union([z.enum(OFFER_FEED_TYPES), z.string()]),
  actionable: z.boolean().optional(),
  actor: OfferFeedActorSchema,
  contest: OfferFeedContestSchema.nullable().optional(),
  commissioner: OfferFeedCommissionerSchema.nullable().optional(),
  entryId: IdSchema.optional(),
  entryLabel: z.string().nullable().optional(),
  shareBps: BasisPointsSchema,
  sharePercent: z.number().optional(),
  priceDollars: z.number(),
  counterShareBps: BasisPointsSchema.nullable().optional(),
  counterPriceDollars: z.number().nullable().optional(),
  conveysPickControl: z.boolean().nullable().optional(),
  status: z.string().optional(),
  expiresAt: IsoDateSchema.nullable().optional(),
  createdAt: IsoDateSchema.optional(),
});

export const OfferFeedEnvelopeSchema = z.object({
  data: z.array(z.unknown()),
  total: z.number().int().nonnegative().optional(),
});

export type OfferFeedItem = z.infer<typeof OfferFeedItemSchema>;

const OFFER_FLOOR_STATUSES = ['active', 'contest_ended', 'marketplace_off'] as const;
const OfferFloorSettingSourceSchema = z.enum(['global', 'contest_type', 'contest', 'default']);

const OfferFloorLevelValuesShape = {
  receiveOffers: z.boolean().nullable(),
  pickControlFilter: OfferPickControlFilterSchema.nullable(),
  rejectOffersBelowEstimatedValuePercent: z.number().nullable(),
  receiveOfferNotifications: z.boolean().nullable(),
  rejectNotificationsBelowEstimatedValuePercent: z.number().nullable(),
  updatedAt: IsoDateSchema.nullable(),
  revision: z.number().int().nonnegative().optional().catch(undefined),
};

export const GlobalOfferFloorLevelSchema = z.object({
  scope: z.literal('global'),
  ...OfferFloorLevelValuesShape,
});

export const ContestOfferFloorLevelSchema = z.object({
  scope: z.literal('contest'),
  contestId: IdSchema,
  contestName: z.string().nullable(),
  status: z.enum(OFFER_FLOOR_STATUSES),
  ...OfferFloorLevelValuesShape,
});

const OfferFloorPlatformDefaultsSchema = z.object({
  receiveOffers: z.boolean(),
  pickControlFilter: OfferPickControlFilterSchema,
  rejectOffersBelowEstimatedValuePercent: z.number().nullable(),
  receiveOfferNotifications: z.boolean(),
  rejectNotificationsBelowEstimatedValuePercent: z.number(),
});

export const OfferFloorsEnvelopeSchema = z.object({
  data: z.array(z.unknown()),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  meta: z.object({
    global: GlobalOfferFloorLevelSchema.nullable(),
    platformDefaults: OfferFloorPlatformDefaultsSchema,
  }),
});

const SwitchSettingSchema = z.object({ value: z.boolean(), source: OfferFloorSettingSourceSchema });

const ResolvedOfferFloorSettingsSchema = z.object({
  receiveOffers: SwitchSettingSchema,
  pickControlFilter: z.object({
    value: OfferPickControlFilterSchema,
    source: OfferFloorSettingSourceSchema,
  }),
  rejectOffersBelowEstimatedValuePercent: z.object({
    value: z.number().nullable(),
    source: OfferFloorSettingSourceSchema,
  }),
  receiveOfferNotifications: SwitchSettingSchema,
  rejectNotificationsBelowEstimatedValuePercent: z.object({
    value: z.number(),
    source: OfferFloorSettingSourceSchema,
  }),
});

export const EffectiveOfferFloorsSchema = z.object({
  contestId: IdSchema,
  contestType: z.string(),
  effective: ResolvedOfferFloorSettingsSchema,
  inherited: ResolvedOfferFloorSettingsSchema,
  estimatedValueCents: z.number().int().nullable(),
  estimatedValueDollars: z.number().nullable(),
  contestLevelRevision: z.number().int().nonnegative().optional().catch(undefined),
});

export const SetOfferFloorRequestSchema = z
  .object({
    receiveOffers: z.boolean().nullable().optional(),
    pickControlFilter: OfferPickControlFilterSchema.nullable().optional(),
    rejectOffersBelowEstimatedValuePercent: OfferFloorPercentSchema.nullable().optional(),
    receiveOfferNotifications: z.boolean().nullable().optional(),
    rejectNotificationsBelowEstimatedValuePercent: OfferFloorPercentSchema.nullable().optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, { message: 'Empty offer floor patch' });

export const MyPortfolioContestSchema = z.object({
  contestId: IdSchema,
  name: z.string(),
  contestType: z.string(),
  league: z.string().nullable(),
  prizePoolCents: CentsSchema,
  prizePoolDollars: z.number(),
  thumbnailUrl: z.string().nullable(),
  myEntryCount: z.number().int().nonnegative(),
  marketplaceStatus: MarketplaceStatusSchema,
  hasCustomSettings: z.boolean(),
});

export const MyPortfolioContestsEnvelopeSchema = z.object({
  data: z.array(z.unknown()),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  degraded: z.array(z.string()).catch([]),
});

export type OfferPickControlFilter = z.infer<typeof OfferPickControlFilterSchema>;
export type SellerMinimum = z.infer<typeof SellerMinimumSchema>;
export type OfferableShare = z.infer<typeof OfferableShareSchema>;
export type OfferNotificationSuppression = NonNullable<PublicOffer['notificationSuppression']>;
export type GlobalOfferFloorLevel = z.infer<typeof GlobalOfferFloorLevelSchema>;
export type ContestOfferFloorLevel = z.infer<typeof ContestOfferFloorLevelSchema>;
export type OfferFloorPlatformDefaults = z.infer<typeof OfferFloorPlatformDefaultsSchema>;
export type EffectiveOfferFloors = z.infer<typeof EffectiveOfferFloorsSchema>;
export type ResolvedOfferFloorSettings = EffectiveOfferFloors['effective'];
export type SetOfferFloorRequest = z.infer<typeof SetOfferFloorRequestSchema>;
export type MyPortfolioContest = z.infer<typeof MyPortfolioContestSchema>;
export type MyPortfolioContestsStatus = 'open' | 'alive' | 'all';
